import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ArrowUpRight, Sparkles, LifeBuoy } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { HELP_SECTIONS } from "@/lib/helpContent";

function matches(text, q) {
  return (text || "").toLowerCase().includes(q);
}

export default function AdminHelp() {
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(() => location.hash.replace("#", "") || HELP_SECTIONS[0].id);
  const refs = useRef({});

  const q = query.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!q) return HELP_SECTIONS;
    return HELP_SECTIONS.map((s) => ({
      ...s,
      articles: s.articles.filter(
        (a) => matches(a.title, q) || a.steps.some((st) => matches(st, q)) || (a.tips || []).some((t) => matches(t, q)) || matches(s.label, q),
      ),
    })).filter((s) => s.articles.length);
  }, [q]);

  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (id && refs.current[id]) {
      refs.current[id].scrollIntoView({ behavior: "smooth", block: "start" });
      setActive(id);
    }
  }, [location.hash]);

  function jump(id) {
    setQuery("");
    setActive(id);
    navigate(`/admin/help#${id}`, { replace: true });
    requestAnimationFrame(() => refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  return (
    <div className="space-y-10" data-testid="admin-help-page">
      <AdminPageHeader breadcrumb={["STUDIO", "HELP"]} title="How-to guides" />

      <div className="relative max-w-2xl">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guides — e.g. password, invoice, domain, favorites…"
          className="input w-full pl-11 h-12"
          data-testid="help-search"
        />
      </div>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-14">
        <nav className="hidden lg:block sticky top-24 self-start space-y-1" data-testid="help-nav">
          {HELP_SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => jump(s.id)}
              className={`block w-full text-left px-3 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                active === s.id ? "text-foreground border-l border-[color:var(--gold-foil)]" : "text-muted-foreground hover:text-foreground border-l border-transparent"
              }`}
              data-testid={`help-nav-${s.id}`}
            >
              {s.label}
            </button>
          ))}
          <div className="pt-6 px-3">
            <Link to="/admin?copilot=1" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold-foil)]" data-testid="help-ask-lumetier">
              <Sparkles className="w-3 h-3" /> Ask Lumetier
            </Link>
          </div>
        </nav>

        <div className="space-y-16">
          {sections.length === 0 && (
            <div className="py-16 text-center" data-testid="help-no-results">
              <LifeBuoy className="w-6 h-6 mx-auto text-muted-foreground mb-4" />
              <p className="font-serif text-2xl">Nothing matches "{query}".</p>
              <p className="text-sm text-muted-foreground mt-2">Try a different word, or <Link to="/admin?copilot=1" className="underline">ask Lumetier</Link> in plain English.</p>
            </div>
          )}
          {sections.map((s) => (
            <section key={s.id} id={s.id} ref={(el) => (refs.current[s.id] = el)} className="scroll-mt-24" data-testid={`help-section-${s.id}`}>
              <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-border">
                <div>
                  <div className="ed-eyebrow text-muted-foreground mb-2">{s.label}</div>
                  <h2 className="font-serif text-3xl leading-tight">{s.summary}</h2>
                </div>
                <Link
                  to={s.path}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={`help-open-${s.id}`}
                >
                  Open {s.label} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="divide-y divide-border">
                {s.articles.map((a) => (
                  <article key={a.id} id={`${s.id}-${a.id}`} className="py-8 grid md:grid-cols-[260px_1fr] gap-4 md:gap-10" data-testid={`help-article-${s.id}-${a.id}`}>
                    <h3 className="font-serif text-xl leading-snug">{a.title}</h3>
                    <div>
                      <ol className="space-y-3">
                        {a.steps.map((st, i) => (
                          <li key={i} className="flex gap-4 text-sm leading-relaxed">
                            <span className="shrink-0 w-6 h-6 inline-flex items-center justify-center border border-border text-[10px] tabular-nums text-muted-foreground">{i + 1}</span>
                            <span>{st}</span>
                          </li>
                        ))}
                      </ol>
                      {a.tips?.length > 0 && (
                        <div className="mt-5 pl-10 space-y-2">
                          {a.tips.map((t, i) => (
                            <p key={i} className="text-xs text-muted-foreground leading-relaxed border-l-2 border-[color:var(--gold-foil)]/50 pl-3">{t}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}

          <div className="border border-[color:var(--gold-foil)]/40 p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="help-still-stuck">
            <div>
              <div className="ed-eyebrow text-[color:var(--gold-foil)] mb-2">Still stuck?</div>
              <p className="font-serif text-2xl">Ask Lumetier in plain English.</p>
              <p className="text-sm text-muted-foreground mt-1">The assistant knows your studio's setup and can walk you through any of these steps.</p>
            </div>
            <Link to="/admin?copilot=1" className="inline-flex items-center gap-2 px-5 h-11 bg-foreground text-background text-[11px] uppercase tracking-[0.24em] hover:opacity-90 transition-opacity shrink-0" data-testid="help-ask-lumetier-cta">
              <Sparkles className="w-3.5 h-3.5" /> Ask Lumetier
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
